import React from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/layout';
import { Button } from '../components/common';

export const About = () => {
  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            About FAD
          </h1>
          <p className="text-xl text-gray-600">
            Fate After Dice - Asynchronous Collaborative Storytelling
          </p>
        </div>

        {/* What is FAD */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">What is FAD?</h2>
          <div className="prose prose-gray max-w-none">
            <p className="text-gray-600 mb-4">
              FAD (Fate After Dice) is an asynchronous, online, multimedia-based collaborative
              storytelling system designed for tabletop roleplaying game (TTRPG) enthusiasts.
              Inspired by the Fate RPG system, FAD enables players across different time zones
              to craft compelling narratives together without the need for synchronized sessions.
            </p>
            <p className="text-gray-600 mb-4">
              Unlike traditional TTRPGs that require everyone to be online simultaneously,
              FAD embraces asynchronous play. Players contribute when it suits them, making
              it perfect for busy schedules and global groups.
            </p>
          </div>
        </section>

        {/* How to Play */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">How to Play</h2>

          <div className="space-y-6">
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-primary-600 mb-2">
                Roles: GM and Players
              </h3>
              <p className="text-gray-600">
                Each game has one <strong>Game Master (GM)</strong> who guides the story and
                sets up scenarios, and multiple <strong>Players</strong> who contribute content
                and shape the narrative through their characters.
              </p>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-primary-600 mb-2">
                Round Structure
              </h3>
              <p className="text-gray-600 mb-3">
                Stories unfold through a series of rounds, each with distinct phases:
              </p>
              <ol className="list-decimal list-inside space-y-2 text-gray-600">
                <li><strong>Setup</strong> - The GM writes a prompt describing the situation</li>
                <li><strong>Influence</strong> - Players spend influence to propose story details</li>
                <li><strong>Submission</strong> - Players contribute prose, images, or audio</li>
                <li><strong>Composition</strong> - The GM weaves submissions into a passage</li>
                <li><strong>Voting</strong> - Players vote for the MVP contributor</li>
                <li><strong>Complete</strong> - Influence is distributed and the round ends</li>
              </ol>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-primary-600 mb-2">
                Multimedia Submissions
              </h3>
              <p className="text-gray-600">
                Players aren't limited to text. FAD supports <strong>prose</strong>,
                <strong> images</strong>, and <strong>audio</strong> submissions. Whether
                you're writing dialogue, sketching a scene, or recording ambient sounds,
                your creativity can shine in multiple formats.
              </p>
            </div>
          </div>
        </section>

        {/* The Influence System */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">The Influence System</h2>

          <div className="bg-primary-50 rounded-lg p-6 mb-6">
            <p className="text-gray-700">
              Influence is the currency of FAD. It represents your ability to shape the story
              beyond your character's direct actions.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h4 className="font-semibold text-gray-900 mb-2">Earning Influence</h4>
              <ul className="list-disc list-inside text-gray-600 space-y-1">
                <li>Win the MVP vote for your round contributions</li>
                <li>Receive the GM's Pick award</li>
                <li>Have your story details accepted</li>
              </ul>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h4 className="font-semibold text-gray-900 mb-2">Spending Influence</h4>
              <ul className="list-disc list-inside text-gray-600 space-y-1">
                <li>Propose story details during the Influence phase</li>
                <li>Bid on details to increase their chances</li>
                <li>Purchase bonus submission units</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Aspects */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Aspects</h2>
          <div className="prose prose-gray max-w-none">
            <p className="text-gray-600 mb-4">
              Borrowed from the Fate system, <strong>Aspects</strong> are short phrases that
              describe important truths about characters, scenes, or the world. They provide
              narrative hooks that players can reference in their submissions.
            </p>
            <p className="text-gray-600">
              Characters have aspects like their <em>High Concept</em> (their defining trait)
              and <em>Trouble</em> (their recurring challenge). Scenes can have aspects that
              describe the environment or situation. Referencing aspects in your submissions
              ties your content to the established fiction.
            </p>
          </div>
        </section>

        {/* Credits */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Credits & Inspiration</h2>
          <div className="prose prose-gray max-w-none">
            <p className="text-gray-600 mb-4">
              FAD draws inspiration from the <strong>Fate RPG system</strong> by Evil Hat
              Productions, particularly its aspect-based narrative mechanics and collaborative
              storytelling philosophy.
            </p>
            <p className="text-gray-600">
              FAD is an open-source project. Contributions, feedback, and suggestions are
              welcome on our{' '}
              <a
                href="https://github.com/jnickg/fad"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-600 hover:text-primary-700"
              >
                GitHub repository
              </a>.
            </p>
          </div>
        </section>

        {/* CTA */}
        <section className="text-center py-8 border-t border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Ready to start your story?
          </h2>
          <div className="flex justify-center gap-4">
            <Link to="/register">
              <Button>Create Account</Button>
            </Link>
            <Link to="/login">
              <Button variant="secondary">Log In</Button>
            </Link>
          </div>
        </section>
      </div>
    </Layout>
  );
};
